/* NAPE storefront — gallery + zoom, bundle sync, add to cart, sticky bar, delivery estimate,
   reveal-on-scroll, hotspots, dial slider, announcement rotation, header + mobile menu,
   scroll-spy, cart-drawer bundle upgrade, sign-up popup.
   No dependencies. Works with Dawn's cart drawer / cart notification when present. */
(() => {
  if (window.__napeInit) return;
  window.__napeInit = true;
  document.documentElement.classList.add('nape-js');

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const smooth = reduceMotion ? 'auto' : 'smooth';

  /* ── Reveal on scroll ─────────────────────────────────────── */
  let revealIO;
  function initReveal(root = document) {
    const els = $$('[data-reveal], [data-nape-load]', root).filter((el) => !el.classList.contains('is-in'));
    if (!('IntersectionObserver' in window) || reduceMotion) { els.forEach((el) => el.classList.add('is-in')); return; }
    revealIO = revealIO || new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-in'); revealIO.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    els.forEach((el) => revealIO.observe(el));
  }

  /* ── Gallery + zoom ───────────────────────────────────────── */
  function initGallery(gallery) {
    if (gallery.dataset.napeReady) return;
    gallery.dataset.napeReady = '1';
    if (reduceMotion) $$('video[autoplay]', gallery).forEach((v) => { v.removeAttribute('autoplay'); v.pause(); });

    const track = $('[data-track]', gallery);
    const slides = $$('.nape-gallery__slide', gallery);
    const thumbs = $$('.nape-gallery__thumb', gallery);
    const prev = $('[data-prev]', gallery);
    const next = $('[data-next]', gallery);
    const cur = $('[data-current]', gallery);
    const total = $('[data-total]', gallery);
    if (!track || !slides.length) return;
    if (total) total.textContent = slides.length;
    if (slides.length < 2) { [prev, next].forEach((b) => b && (b.hidden = true)); $('.nape-gallery__count', gallery)?.setAttribute('hidden', ''); }

    let index = 0;
    // `follow` scrolls the thumbnail strip to the active thumb. Not on start-up: it reads layout, which
    // at that moment forces a full-page layout inside the script and delays the first paint of the hero.
    const setActive = (i, follow = true) => {
      index = Math.max(0, Math.min(slides.length - 1, i));
      thumbs.forEach((t, n) => {
        t.classList.toggle('is-active', n === index);
        if (n === index) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
      });
      if (cur) cur.textContent = index + 1;
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;
      const active = thumbs[index];
      if (follow && active && active.parentElement) {
        const p = active.parentElement;
        const vertical = p.scrollHeight > p.clientHeight + 4;
        const ar = active.getBoundingClientRect();
        const pr = p.getBoundingClientRect();
        p.scrollTo(vertical
          ? { top: ar.top - pr.top + p.scrollTop - 8, behavior: smooth }
          : { left: ar.left - pr.left + p.scrollLeft - 8, behavior: smooth });
      }
    };
    const go = (i) => {
      track.scrollTo({ left: track.clientWidth * i, behavior: smooth });
      setActive(i);
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => go(Number(thumb.dataset.index))));
    prev && prev.addEventListener('click', () => go(index - 1));
    next && next.addEventListener('click', () => go(index + 1));

    let raf;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setActive(Math.round(track.scrollLeft / track.clientWidth)));
    }, { passive: true });
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
    });
    setActive(0, false);

    const dialog = $('[data-nape-zoom]');
    if (dialog && typeof dialog.showModal === 'function') {
      const img = $('[data-zoom-img]', dialog);
      slides.forEach((s) => {
        if (!s.dataset.zoom) return;
        s.addEventListener('click', () => { img.src = s.dataset.zoom; dialog.showModal(); });
      });
      $('[data-zoom-close]', dialog).addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
    }
  }

  /* ── Bundles: keep every selector, button, save line and the sticky bar in sync ── */
  const forms = () => $$('.nape-form');
  const primaryForm = () => $('.nape-form:not(.nape-form--secondary)') || forms()[0];

  // A bundle is a quantity of the one variant: the radios are name="quantity", the variant id is a hidden input.
  const BUNDLE = '.nape-bundle input[name="quantity"]';

  function selectedInput(form) {
    return $$(`${BUNDLE}:checked`).find((i) => i.form === form && !i.disabled)
      || $$(BUNDLE).find((i) => i.form === form && !i.disabled)
      || null;
  }

  function syncFromValue(value) {
    let chosen;
    $$(BUNDLE).forEach((input) => {
      if (input.value === String(value) && !input.disabled) { input.checked = true; chosen = input; }
    });
    if (!chosen) return;

    forms().forEach((form) => {
      const price = $('[data-nape-price]', form);
      if (price) price.textContent = chosen.dataset.price;
      const save = $('[data-nape-save-line]', form);
      if (save) save.textContent = chosen.dataset.save ? `You save ${chosen.dataset.save} with this bundle` : '';
    });

    const hero = $('[data-nape-hero-price]');
    if (hero) {
      const units = Number(chosen.dataset.units) || 1;
      $('[data-price-now]', hero).textContent = chosen.dataset.price;
      const was = $('[data-price-was]', hero);
      if (was) {
        was.hidden = !chosen.dataset.compare;
        was.innerHTML = chosen.dataset.compare ? `<span class="visually-hidden">Regular price </span>${chosen.dataset.compare}` : '';
      }
      $('[data-price-for]', hero).textContent = units > 1 ? `for ${units} · ${chosen.dataset.each} each` : '';
    }

    const select = $('[data-nape-sticky-select]');
    if (select) select.value = String(value);
    const sp = $('[data-nape-sticky-price]');
    if (sp) sp.textContent = chosen.dataset.price;
  }

  document.addEventListener('change', (e) => {
    const el = e.target;
    if (el.matches && el.matches(BUNDLE)) syncFromValue(el.value);
    if (el.matches && el.matches('[data-nape-sticky-select]')) syncFromValue(el.value);
  });

  /* ── Add to cart ─────────────────────────────────────────── */
  function cartUI() {
    const el = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    return el && typeof el.renderContents === 'function' && typeof el.getSectionsToRender === 'function' ? el : null;
  }

  const inFlight = new WeakSet();
  function setBusy(el, busy) {
    if (!el) return;
    if (busy) { el.setAttribute('aria-busy', 'true'); el.disabled = true; }
    else { el.removeAttribute('aria-busy'); el.disabled = false; }
  }
  function flashAdded(el) {
    if (!el) return;
    const label = $('.nape-btn__label', el);
    if (!label) return;
    const text = label.textContent;
    el.classList.add('is-added');
    label.textContent = 'Added to cart';
    setTimeout(() => { el.classList.remove('is-added'); label.textContent = text; }, 1800);
  }

  async function addToCart(form, trigger) {
    if (inFlight.has(form)) return;
    const idInput = $$('input[name="id"]').find((i) => i.form === form);
    if (!idInput) return;
    const input = selectedInput(form);
    const quantity = Math.max(1, Number(input && input.value) || 1);

    const button = $('[data-nape-submit]', form);
    const error = $('[data-nape-error]', form);
    const feedback = trigger || button;
    inFlight.add(form);
    setBusy(button, true);
    if (trigger && trigger !== button) setBusy(trigger, true);
    if (error) error.textContent = '';

    const body = new FormData();
    body.append('id', idInput.value);
    body.append('quantity', String(quantity));
    const cart = cartUI();
    if (cart) {
      // Dawn sends the ids once, comma-joined. Repeated keys would only keep the last one.
      body.append('sections', cart.getSectionsToRender().map((s) => s.id).join(','));
      body.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(feedback);
    }

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const cartUrl = (window.routes && window.routes.cart_url) || `${root}cart`;
    let added = false;
    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      // A rate limit, bot challenge or 5xx answers with HTML: never show the parser's error to a shopper.
      const json = await res.json().catch(() => ({ status: res.status || 500 }));
      if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not add to cart. Please try again.');
      added = true;
      flashAdded(feedback);
      document.dispatchEvent(new CustomEvent('nape:added', { detail: json }));
      store.set(sessionStorage, 'nape_added', '1');

      if (!cart) { setTimeout(() => { window.location.href = cartUrl; }, 450); return; }
      try {
        cart.classList.remove('is-empty');
        cart.renderContents(json);
      } catch (renderErr) {
        // The item IS in the cart; never show an error that invites a second add.
        window.location.href = cartUrl;
      }
    } catch (err) {
      if (!added && error) {
        error.textContent = err.message;
        if (trigger && !form.contains(trigger)) error.scrollIntoView({ behavior: smooth, block: 'center' });
      }
    } finally {
      inFlight.delete(form);
      setBusy(button, false);
      if (trigger && trigger !== button) setBusy(trigger, false);
    }
  }

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.classList || !form.classList.contains('nape-form')) return;
    e.preventDefault();
    addToCart(form, e.submitter);
  });

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest && e.target.closest('[data-nape-buy]');
    if (!trigger) return;
    e.preventDefault();
    const form = primaryForm();
    if (form) addToCart(form, trigger);
    else if (trigger.dataset.href) window.location.href = trigger.dataset.href; // no buy box on this page
    else window.location.hash = 'nape-buy';
  });

  /* Cart drawer checkout: go straight to /checkout instead of relying on Dawn's form submit, which can be
     swallowed while the drawer is still animating in. Shows a spinner so a tap always visibly does something.
     In the theme editor Shopify blocks checkout, so say that instead of failing silently. */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('#CartDrawer-Checkout, #checkout');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    if (window.Shopify && window.Shopify.designMode) {
      let note = document.getElementById('NapeCheckoutNote');
      if (!note) {
        note = document.createElement('p');
        note.id = 'NapeCheckoutNote';
        note.className = 'nape-checkout-note';
        note.setAttribute('role', 'status');
        note.textContent = 'Checkout is switched off inside the theme editor. Open the theme with Preview to test it.';
        btn.insertAdjacentElement('afterend', note);
      }
      return;
    }
    if (btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('is-loading');
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    window.location.href = `${root}checkout`;
    // If the page is restored from the back/forward cache, the button must work again.
    window.addEventListener('pageshow', () => { btn.removeAttribute('aria-busy'); btn.classList.remove('is-loading'); }, { once: true });
  });

  /* ── Sticky bar ──────────────────────────────────────────── */
  function initSticky() {
    const sticky = $('[data-nape-sticky]');
    const form = primaryForm();
    const anchor = form && $('[data-nape-submit]', form);
    if (!sticky || !anchor || !('IntersectionObserver' in window)) return;
    if (sticky._napeIO) sticky._napeIO.disconnect();
    sticky.hidden = false;
    sticky.inert = true;

    const others = $$('.nape-form--secondary [data-nape-submit], .nape-final [data-nape-buy], .nape-hh__cta, .nape-rv__cta .nape-btn');
    const visible = new Set();
    const phone = window.matchMedia('(max-width: 749px)');
    const update = () => {
      // Phones: a buy button is always within thumb reach, from the first screen on.
      // Wider screens: only once the main button has scrolled away.
      const heroGone = !visible.has(anchor) && (phone.matches || anchor.getBoundingClientRect().top < 0);
      const otherShowing = others.some((b) => visible.has(b));
      const show = heroGone && !otherShowing;
      sticky.classList.toggle('is-visible', show);
      sticky.inert = !show;
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => (en.isIntersecting ? visible.add(en.target) : visible.delete(en.target)));
      update();
    });
    [anchor, ...others].forEach((b) => io.observe(b));
    sticky._napeIO = io;
  }

  /* ── Delivery estimate (merchant-set business-day range) ──── */
  function addBusinessDays(date, n) {
    const d = new Date(date);
    let added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return d;
  }
  function initEta() {
    $$('[data-nape-eta]').forEach((el) => {
      const min = Number(el.dataset.min) || 0;
      const max = Number(el.dataset.max) || 0;
      const cutoff = Number(el.dataset.cutoff) || 0;
      if (!max) return;
      const text = $('[data-eta-text]', el);
      const fmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
      const render = () => {
        const now = new Date();
        const isBizDay = now.getDay() !== 0 && now.getDay() !== 6;
        let lead = 'Order now';
        if (cutoff && isBizDay && now.getHours() < cutoff) {
          const end = new Date(now); end.setHours(cutoff, 0, 0, 0);
          const mins = Math.max(0, Math.round((end - now) / 60000));
          const h = Math.floor(mins / 60);
          const m = mins % 60;
          lead = `Order in the next <strong>${h ? `${h}h ` : ''}${m}m</strong> to ship today`;
        }
        const from = fmt.format(addBusinessDays(now, Math.max(min, 1)));
        const to = fmt.format(addBusinessDays(now, max));
        text.innerHTML = `${lead} · arrives <strong>${from === to ? from : `${from} – ${to}`}</strong>`;
      };
      render();
      el.hidden = false;
      setInterval(render, 60000);
    });
  }

  /* ── Mechanism hotspots + dial slider ────────────────────── */
  function initMechanism(root = document) {
    $$('.nape-mech, [data-nape-anatomy]', root).forEach((sec) => {
      if (sec.dataset.napeReady) return;
      sec.dataset.napeReady = '1';
      const spots = $$('[data-hotspot]', sec);
      const callouts = $$('[data-callout]', sec);
      const activate = (i) => {
        spots.forEach((s) => s.classList.toggle('is-active', s.dataset.hotspot === String(i)));
        callouts.forEach((c) => c.classList.toggle('is-active', c.dataset.callout === String(i)));
      };
      spots.forEach((s) => s.addEventListener('click', () => {
        activate(s.dataset.hotspot);
        const c = callouts.find((x) => x.dataset.callout === s.dataset.hotspot);
        if (c && window.innerWidth < 990) c.scrollIntoView({ behavior: smooth, block: 'center' });
      }));
      callouts.forEach((c) => c.addEventListener('mouseenter', () => activate(c.dataset.callout)));
      activate(0);
    });
    $$('[data-nape-slider]', root).forEach((slider) => {
      const range = $('input[type="range"]', slider);
      if (!range || slider.dataset.napeReady) return;
      slider.dataset.napeReady = '1';
      const set = () => slider.style.setProperty('--pos', `${range.value}%`);
      range.addEventListener('input', set);
      set();
    });
  }

  /* Shopify's preview bar (unpublished themes) is a fixed iframe pinned to the bottom of the screen.
     It sits over the cart drawer's checkout button and the sticky add-to-cart bar, so taps there do
     nothing while previewing. Measure it and lift our fixed UI above it. Published themes: no bar, no-op. */
  function initPreviewBar() {
    const root = document.documentElement;
    let lastH = 0;
    const apply = () => {
      const bar = $('#PBarNextFrameWrapper') || $('#PBarFrameWrapper') || $('.shopify-pbar');
      const h = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
      if (h === lastH) return; // a custom property on <html> restyles the whole page: only when it changed
      lastH = h;
      root.classList.toggle('nape-pbar', h > 0);
      root.style.setProperty('--pbar-h', `${h}px`);
    };
    apply();
    // The bar is injected after load, and it resizes when collapsed.
    [400, 1200, 3000].forEach((ms) => setTimeout(apply, ms));
    if ('ResizeObserver' in window) {
      const watch = () => {
        const bar = $('#PBarNextFrameWrapper') || $('#PBarFrameWrapper') || $('.shopify-pbar');
        if (bar) new ResizeObserver(apply).observe(bar);
      };
      setTimeout(watch, 1500);
    }
    window.addEventListener('resize', apply);
  }

  /* Policy pages: many policies are pasted as plain paragraphs. Short lines that read like headings
     ("1. Shipping Regions", "OVERVIEW", "Returns") get heading styles; a first line that repeats the page
     title is hidden. Text is never changed. */
  function initPolicy() {
    const body = $('.shopify-policy__body .rte') || $('.shopify-policy__body');
    if (!body) return;
    const title = ($('.shopify-policy__title h1') || {}).textContent || '';
    const norm = (t) => t.toLowerCase().replace(/[^a-z]/g, '');
    const looksLikeHeading = (t) => {
      if (!t || /[.!?:;,]$/.test(t)) return false;
      const words = t.split(' ');
      const numbered = /^(\d{1,2}|[ivx]{1,4})[.)]\s+\S/i.test(t) && t.length <= 70;
      const caps = t.length >= 4 && t.length <= 70 && t === t.toUpperCase() && /[A-Z]/.test(t);
      const titleish = t.length <= 48 && words.length <= 7 && /^[A-Z]/.test(t);
      return numbered || caps || titleish;
    };
    $$(':scope > p', body).forEach((p, i) => {
      const t = p.textContent.replace(/\s+/g, ' ').trim();
      if (!t) { p.classList.add('nape-pol-empty'); return; }
      if (i < 2 && norm(t) === norm(title)) { p.classList.add('nape-pol-dup'); return; }
      // Whole paragraph is a heading.
      if (!p.querySelector('br') && t.length <= 70 && looksLikeHeading(t)) { p.classList.add('nape-pol-h'); return; }
      // "Heading<br>Body text" inside one paragraph (often many in a row): style each such line as a heading.
      const isBreak = (n) => !!n && (n.nodeName === 'BR' || (n.nodeType === 1 && !n.textContent.trim() && !!n.querySelector('br')));
      const isBlank = (n) => !!n && !isBreak(n) && !n.textContent.trim();
      const kids = Array.from(p.childNodes);
      kids.forEach((n, k) => {
        if (n.nodeType !== 1 || isBreak(n) || n.querySelector('br')) return;
        const text = n.textContent.replace(/\s+/g, ' ').trim();
        if (!looksLikeHeading(text)) return;
        let before = k - 1; while (before >= 0 && isBlank(kids[before])) before -= 1;
        let after = k + 1; while (after < kids.length && isBlank(kids[after])) after += 1;
        if ((before < 0 || isBreak(kids[before])) && isBreak(kids[after])) {
          n.classList.add('nape-pol-lead');
          const br = kids[after].nodeName === 'BR' ? kids[after] : kids[after].querySelector('br');
          if (br) br.classList.add('nape-pol-gone');
        }
      });
    });
  }

  /* Review wall: filter chips, "show more", long-review toggle. */
  function initReviews(root) {
    const cards = $$('[data-rv-card]', root);
    if (!cards.length) return;
    const moreWrap = $('[data-rv-more-wrap]', root);
    const empty = $('[data-rv-empty]', root);
    const step = () => (window.matchMedia('(min-width: 990px)').matches ? 6 : window.matchMedia('(min-width: 750px)').matches ? 4 : 3);
    let filter = 'all';
    let limit = step();
    const draw = () => {
      const matches = cards.filter((c) => filter === 'all' || c.dataset.tag === filter);
      cards.forEach((c) => { c.hidden = true; });
      matches.slice(0, limit).forEach((c) => { c.hidden = false; });
      if (moreWrap) moreWrap.hidden = matches.length <= limit;
      if (empty) empty.hidden = matches.length > 0;
    };
    $$('[data-rv-filter]', root).forEach((btn) => btn.addEventListener('click', () => {
      filter = btn.dataset.rvFilter;
      limit = step();
      $$('[data-rv-filter]', root).forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
      draw();
    }));
    const more = $('[data-rv-show-more]', root);
    if (more) more.addEventListener('click', () => {
      const before = cards.filter((c) => !c.hidden).length;
      limit += step();
      draw();
      const next = cards.filter((c) => !c.hidden)[before];
      if (next) { next.setAttribute('tabindex', '-1'); next.focus({ preventScroll: true }); }
    });
    $$('[data-rv-more]', root).forEach((btn) => btn.addEventListener('click', () => {
      const quote = btn.previousElementSibling;
      const open = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', String(open));
      btn.textContent = open ? 'Show less' : 'Read more';
      if (quote) quote.classList.toggle('is-clamped', !open);
    }));
    draw();
  }

  /* Comparison: on phones show NAPE against one alternative at a time. */
  function initCompare(root) {
    const picks = $$('[data-col-pick]', root);
    if (!picks.length) return;
    const show = (col) => {
      picks.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.colPick === col)));
      $$('[data-col]', root).forEach((cell) => cell.classList.toggle('is-off', cell.dataset.col !== col));
    };
    picks.forEach((b) => b.addEventListener('click', () => show(b.dataset.colPick)));
  }

  /* Ticker ----------------------------------------------------------------
     A seamless loop needs (a) enough copies to cover the screen plus one, and
     (b) a shift of exactly one copy. Both depend on real widths, so measure. */
  function initMarquee(el) {
    const track = $('.nape-marquee__track', el);
    const first = track && $('.nape-marquee__list', track);
    if (!first) return;
    const pxPerSec = 1400 / (Number(el.dataset.speed) || 40); // same pace whatever the list length
    const layout = () => {
      const w = first.getBoundingClientRect().width;
      if (!w) return;
      const need = Math.ceil(el.clientWidth / w) + 1;
      let lists = $$('.nape-marquee__list', track);
      while (lists.length < need) {
        const clone = first.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
        lists = $$('.nape-marquee__list', track);
      }
      track.style.setProperty('--shift', `${-w}px`);
      track.style.animationDuration = `${Math.max(8, w / pxPerSec)}s`;
    };
    requestAnimationFrame(layout);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(layout);
    let t;
    window.addEventListener('resize', () => { clearTimeout(t); t = setTimeout(layout, 150); });
  }

  /* ── Announcement rotation (below 1100px) ───────────────── */
  function initAnnouncement(bar) {
    const msgs = $$('.nape-announce__msg', bar);
    if (msgs.length < 2 || reduceMotion || bar.dataset.napeReady) return;
    bar.dataset.napeReady = '1';
    const mq = window.matchMedia('(max-width: 1099px)');
    let i = 0;
    let timer;
    const ms = (Number(bar.dataset.napeRotate) || 4) * 1000;
    const next = () => {
      msgs[i].classList.remove('is-active');
      i = (i + 1) % msgs.length;
      msgs[i].classList.add('is-active');
    };
    const apply = () => {
      clearInterval(timer);
      bar.classList.toggle('is-rotating', mq.matches);
      msgs.forEach((m, n) => m.classList.toggle('is-active', n === i));
      if (mq.matches) timer = setInterval(next, ms);
    };
    mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);
    apply();
  }

  /* ── Safe storage ────────────────────────────────────────── */
  const store = {
    get(area, key) { try { return area.getItem(key); } catch (e) { return null; } },
    set(area, key, val) { try { area.setItem(key, val); } catch (e) { /* private mode */ } },
  };

  /* ── Header: scrolled state, mobile menu, scroll-spy ─────── */
  function initHeader() {
    const header = $('[data-nape-header]');
    if (!header || header.dataset.napeReady) return;
    header.dataset.napeReady = '1';

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { header.classList.toggle('is-scrolled', window.scrollY > 8); ticking = false; });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    const menu = $('[data-nape-menu]');
    const opener = $('[data-nape-menu-open]', header);
    if (menu && opener && typeof menu.showModal === 'function') {
      opener.addEventListener('click', () => { menu.showModal(); opener.setAttribute('aria-expanded', 'true'); });
      menu.addEventListener('close', () => { opener.setAttribute('aria-expanded', 'false'); });
      menu.addEventListener('click', (e) => {
        if (e.target === menu || e.target.closest('[data-nape-menu-close]')) menu.close();
      });
    }

    // Highlight the nav link whose section is on screen (homepage anchors).
    const links = $$('[data-nape-navlink]', header).filter((a) => {
      try {
        const u = new URL(a.href, window.location.href);
        return u.hash && u.pathname === window.location.pathname && document.getElementById(u.hash.slice(1));
      } catch (e) { return false; }
    });
    if (!links.length || !('IntersectionObserver' in window)) return;
    const byId = new Map(links.map((a) => [new URL(a.href, window.location.href).hash.slice(1), a]));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const a = byId.get(en.target.id);
        if (!a) return;
        if (en.isIntersecting) {
          links.forEach((l) => { l.classList.remove('is-current'); l.removeAttribute('aria-current'); });
          a.classList.add('is-current');
          a.setAttribute('aria-current', 'true');
        } else if (a.classList.contains('is-current')) {
          a.classList.remove('is-current');
          a.removeAttribute('aria-current');
        }
      });
    }, { rootMargin: '-40% 0px -55% 0px' });
    byId.forEach((a, id) => io.observe(document.getElementById(id)));
  }

  /* ── Cart drawer: one-tap bundle upgrade (real prices only) ─ */
  let lastQtyChange = 0;
  document.addEventListener('change', (e) => { if (e.target.closest && e.target.closest('cart-drawer .quantity')) lastQtyChange = Date.now(); });
  document.addEventListener('click', (e) => { if (e.target.closest && e.target.closest('cart-drawer .quantity__button')) lastQtyChange = Date.now(); }, true);
  function refocusDrawer(message) {
    // The drawer's HTML was replaced: re-trap focus inside it and announce the change.
    setTimeout(() => {
      const container = document.getElementById('CartDrawer');
      if (!container) return;
      const target = container.querySelector('.cart__checkout-button') || container.querySelector('.drawer__inner');
      if (typeof window.trapFocus === 'function') window.trapFocus(container, target);
      else if (target) target.focus();
      const live = document.getElementById('CartDrawer-LiveRegionText');
      if (live) live.textContent = message;
    }, 60);
  }
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest && e.target.closest('[data-nape-upgrade]');
    if (!btn || btn.getAttribute('aria-busy') === 'true') return;
    e.preventDefault();
    const cart = cartUI();
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    // Dawn may still be saving a quantity change; never act on a stale drawer.
    if (document.querySelector('.cart__items--disabled') || Date.now() - lastQtyChange < 600) return;
    const sections = cart ? cart.getSectionsToRender().map((x) => x.id) : undefined;
    const rerender = async () => {
      const r = await fetch(`${root}cart/update.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: {}, sections, sections_url: window.location.pathname }),
      });
      const j = await r.json();
      if (cart && j.sections) { cart.renderContents(j); refocusDrawer('Cart updated'); } else window.location.reload();
    };
    setBusy(btn, true);
    try {
      // Re-check the live cart: the offer must still match exactly what the shopper has.
      const live = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
      const line = (live.items || []).find((i) => i.key === btn.dataset.line);
      if (!line || String(line.quantity) !== btn.dataset.fromQty) {
        await rerender();
        return;
      }
      const res = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: Number(btn.dataset.qty), sections, sections_url: window.location.pathname }),
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || 'Could not update the cart.');
      document.dispatchEvent(new CustomEvent('nape:upgraded', { detail: json }));
      if (cart && json.sections) { cart.renderContents(json); refocusDrawer('Bundle updated'); } else window.location.reload();
    } catch (err) {
      setBusy(btn, false);
      // Shopify said no (stock, quantity limit): say why, in the drawer's own error line. Anything else: the cart page.
      const box = document.getElementById('CartDrawer-CartErrors');
      if (box && err && err.message && !(err instanceof SyntaxError) && !(err instanceof TypeError)) box.textContent = err.message;
      else window.location.href = (window.routes && window.routes.cart_url) || `${root}cart`;
    }
  });

  /* ── Sign-up popup: delayed / exit intent, once per visit ── */
  function initPopup() {
    const pop = $('[data-nape-popup]');
    if (!pop || pop.dataset.napeReady || typeof pop.showModal !== 'function') return;
    pop.dataset.napeReady = '1';

    const close = () => { if (pop.open) pop.close(); };
    pop.addEventListener('click', (e) => { if (e.target === pop || e.target.closest('[data-nape-popup-close]')) close(); });
    // Any successful sign-up on this page (popup, section or footer) marks the visitor as subscribed.
    if (/customer_posted=true/.test(window.location.search) && document.querySelector('.nape-signup__ok')) {
      store.set(localStorage, 'nape_signed', '1');
    }
    // Coming back from this popup's form: show its confirmation or its error.
    if (/NapePopup/.test(window.location.hash) && ($('[data-nape-popup-success]', pop) || $('[data-nape-popup-error]', pop))) {
      pop.showModal();
      return;
    }
    if (pop.dataset.enabled !== 'true') return;
    if (Number(pop.dataset.cartCount) > 0) return; // already shopping: don't interrupt
    if (store.get(localStorage, 'nape_signed') || store.get(sessionStorage, 'nape_pop_seen')) return;

    const started = Date.now();
    let timer;
    const open = () => {
      if (store.get(sessionStorage, 'nape_added') || store.get(sessionStorage, 'nape_pop_seen')) return cleanup();
      if (document.querySelector('dialog[open], cart-drawer.active')) return; // try again later
      store.set(sessionStorage, 'nape_pop_seen', '1');
      pop.showModal();
      cleanup();
    };
    const onLeave = (e) => { if (!e.relatedTarget && e.clientY <= 0 && Date.now() - started > 6000) open(); };
    const cleanup = () => { clearInterval(timer); document.removeEventListener('mouseout', onLeave); };
    const delay = (Number(pop.dataset.delay) || 30) * 1000;
    timer = setInterval(() => { if (Date.now() - started >= delay) open(); }, 1000);
    if (pop.dataset.exit === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.addEventListener('mouseout', onLeave);
    }
  }

  // Theme editor: preview the popup when its section is selected.
  document.addEventListener('shopify:section:select', (e) => {
    const pop = $('[data-nape-popup]', e.target);
    if (pop && !pop.open && typeof pop.showModal === 'function') pop.showModal();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const pop = $('[data-nape-popup]', e.target);
    if (pop && pop.open) pop.close();
  });

  /* ── Cart drawer quotes ──────────────────────────────────── */
  // The drawer is re-rendered on every cart change, so this works on whatever card is in the DOM now.
  function showProof(card, i) {
    const slides = $$('[data-proof-slide]', card);
    if (!slides.length) return;
    const n = ((i % slides.length) + slides.length) % slides.length;
    slides.forEach((s, k) => { s.classList.toggle('is-on', k === n); s.setAttribute('aria-hidden', k === n ? 'false' : 'true'); });
    $$('[data-proof-dot]', card).forEach((d, k) => d.classList.toggle('is-on', k === n));
    card.dataset.proofAt = n;
  }
  function initCartProof() {
    document.addEventListener('click', (e) => {
      const dot = e.target.closest('[data-proof-dot]');
      if (!dot) return;
      const card = dot.closest('[data-nape-proof]');
      card.dataset.proofHold = Date.now();
      showProof(card, +dot.dataset.proofDot);
    });
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    setInterval(() => {
      $$('[data-nape-proof]').forEach((card) => {
        if (!card.offsetParent) return; // drawer closed
        if (Date.now() - (+card.dataset.proofHold || 0) < 12000) return; // shopper just picked one
        showProof(card, (+card.dataset.proofAt || 0) + 1);
      });
    }, 5500);
  }

  /* ── Listicle: reading progress + sticky CTA ─────────────── */
  function initListicle() {
    // "Ships by <date>" on the buy box: a real date from the owner's handling time, never a countdown.
    $$('[data-lst-ship]').forEach((el) => {
      const days = Number(el.dataset.lstShip) || 0;
      const tpl = el.dataset.lstShiptext || '';
      const span = $('span', el);
      if (!days || !span || tpl.indexOf('{date}') < 0) return;
      const fmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
      const safe = tpl.replace(/[<>&]/g, '');
      span.innerHTML = safe.replace('{date}', `<b>${fmt.format(addBusinessDays(new Date(), days))}</b>`);
    });

    const article = $('[data-nape-listicle]');
    if (!article) return;
    const bar = $('[data-lst-progress]');
    const sticky = $('[data-lst-sticky]');
    const hero = $('[data-lst-hero]', article);
    const ctas = $$('[data-lst-cta]');
    const update = () => {
      const vh = window.innerHeight;
      const rect = article.getBoundingClientRect();
      const total = rect.height - vh;
      const done = total > 0 ? Math.min(Math.max(-rect.top / total, 0), 1) : 0;
      if (bar) bar.style.width = (done * 100).toFixed(1) + '%';
      // The bar follows once the hero is gone, steps aside while any on-page CTA is on screen,
      // and comes back for readers still going through reviews and FAQ.
      if (sticky) {
        const pastHero = hero ? hero.getBoundingClientRect().bottom < 0 : done > 0.05;
        const ctaOnScreen = ctas.some((c) => {
          const r = c.getBoundingClientRect();
          return r.top < vh && r.bottom > 0;
        });
        sticky.classList.toggle('is-on', pastHero && !ctaOnScreen);
      }
    };
    update();
    window.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
  }

  /* ── Advertorial: UTM pass-through, CTA ids, scroll milestones, sticky bar ── */
  function advTrack(name, data) {
    const payload = Object.assign({ page: 'advertorial', path: location.pathname }, data || {});
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, payload); } catch (e) { /* analytics is optional */ }
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, payload)); } catch (e) { /* ignore */ }
    try { if (typeof window.fbq === 'function') window.fbq('trackCustom', name, payload); } catch (e) { /* ignore */ }
  }

  function initAdvertorial() {
    const root = $('[data-nape-adv]');
    if (!root || root.dataset.napeReady) return;
    root.dataset.napeReady = '1';

    // Every button keeps the ad's UTMs and click ids, so the PDP and checkout are attributed to the ad.
    const incoming = new URLSearchParams(location.search);
    const keep = [...incoming.keys()].filter((k) => /^(utm_|fbclid|gclid|ttclid|msclkid|wbraid|gbraid|sc_click|irclickid|ad_|campaign)/i.test(k));
    $$('[data-adv-cta]').forEach((a) => {
      if (keep.length) {
        try {
          const u = new URL(a.getAttribute('href'), location.origin);
          keep.forEach((k) => { if (!u.searchParams.has(k)) u.searchParams.set(k, incoming.get(k)); });
          a.setAttribute('href', u.pathname + u.search + u.hash);
        } catch (e) { /* leave the link as it is */ }
      }
      a.addEventListener('click', () => advTrack('advertorial_cta_click', { cta_id: a.dataset.ctaId || 'cta' }));
    });

    const sticky = $('[data-adv-sticky]');
    const after = Number(root.dataset.stickyAfter) || 4;
    const trigger = $(`[data-adv-reason="${after}"]`, root);
    const finalBlock = $('[data-adv-final]', root);
    const reason4 = $('[data-adv-reason="4"]', root);
    const tests = $('[data-adv-mark]', root);
    const fired = {};
    const once = (key, name, data) => { if (!fired[key]) { fired[key] = true; advTrack(name, data); } };

    const update = () => {
      const vh = window.innerHeight;
      const r = root.getBoundingClientRect();
      const total = r.height - vh;
      const pct = total > 0 ? Math.min(Math.max(-r.top / total, 0), 1) : 1;
      [25, 50, 75, 100].forEach((p) => { if (pct * 100 >= p - 0.5) once(`d${p}`, 'advertorial_scroll', { depth: p }); });
      if (reason4 && reason4.getBoundingClientRect().top < vh * 0.6) once('r4', 'advertorial_reached_reason_4');
      if (tests && tests.getBoundingClientRect().top < vh * 0.6) once('tests', 'advertorial_reached_tests', { mark: tests.dataset.advMark });
      if (sticky) {
        const passed = trigger ? trigger.getBoundingClientRect().top < vh * 0.5 : pct > 0.4;
        let finalOn = false;
        if (finalBlock) { const f = finalBlock.getBoundingClientRect(); finalOn = f.top < vh && f.bottom > 0; }
        const blocks = $$('[data-adv-ctablock]', root).some((b) => { const q = b.getBoundingClientRect(); return q.top < vh && q.bottom > 0 && b.offsetParent !== null && b.closest('.adv-side') === null; });
        sticky.classList.toggle('is-on', passed && !finalOn && !blocks);
      }
    };
    let ticking = false;
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(() => { ticking = false; update(); }); } };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  /* ── Boot ────────────────────────────────────────────────── */
  function init() {
    initHeader();
    initPopup();
    initPolicy();
    initPreviewBar();
    initListicle();
    initAdvertorial();
    initCartProof();
    $$('[data-nape-gallery]').forEach(initGallery);
    $$('[data-nape-rotate]').forEach(initAnnouncement);
    $$('[data-nape-marquee]').forEach(initMarquee);
    $$('[data-nape-compare]').forEach(initCompare);
    $$('[data-nape-reviews]').forEach(initReviews);
    initReveal();
    initMechanism();
    initEta();
    const form = primaryForm();
    const first = form && selectedInput(form);
    if (first) syncFromValue(first.value, false);
    initSticky();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-init only what was re-rendered.
  document.addEventListener('shopify:section:load', (e) => {
    initHeader();
    initPopup();
    $$('[data-nape-gallery]', e.target).forEach(initGallery);
    $$('[data-nape-rotate]', e.target).forEach(initAnnouncement);
    $$('[data-nape-marquee]', e.target).forEach(initMarquee);
    $$('[data-nape-compare]', e.target).forEach(initCompare);
    $$('[data-nape-reviews]', e.target).forEach(initReviews);
    initReveal(e.target);
    initMechanism(e.target);
    const form = primaryForm();
    const first = form && selectedInput(form);
    if (first) syncFromValue(first.value, false);
    initSticky();
  });
})();
